import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Lock } from "lucide-react";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login, loginManager } = useAuth();
  const [tab, setTab] = useState("admin");
  const [email, setEmail] = useState("admin@dakarstreetfood.sn");
  const [password, setPassword] = useState("DakarAdmin2026!");
  const [loading, setLoading] = useState(false);

  const onTab = (t) => {
    setTab(t);
    if (t === "manager") { setEmail(""); setPassword(""); }
    else { setEmail("admin@dakarstreetfood.sn"); setPassword("DakarAdmin2026!"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let u;
      if (tab === "manager") {
        u = await loginManager(email, password);
      } else {
        u = await login(email, password);
      }
      if (u.role !== "admin" && u.role !== "manager") {
        toast.error("Staff access required");
        setLoading(false);
        return;
      }
      toast.success(`Welcome ${u.name || u.email}`);
      navigate("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="admin-login-page">
      {/* Left visual */}
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1633292911330-1ea4da805d27?w=1400"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--dsf-green)]/95 to-[var(--dsf-green)]/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="font-serif text-2xl">Dakar Street Food</Link>
          <div>
            <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--dsf-orange)] mb-4">Espace administrateur</div>
            <h1 className="font-serif text-5xl xl:text-7xl leading-[1.05] max-w-lg">
              Le tableau de bord d'une grande maison.
            </h1>
            <p className="text-white/70 mt-6 max-w-md">
              Pilotez vos univers, vos commandes et votre catalogue depuis une interface professionnelle conçue pour les exigences d'aujourd'hui.
            </p>
          </div>
          <div className="text-xs text-white/40">© 2026 — Maison Dakar Street Food</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 md:p-16 bg-[var(--dsf-sand)]">
        <form onSubmit={submit} className="w-full max-w-md">
          <div className="flex items-center gap-2 text-[var(--dd-mustard-dark)] text-[11px] uppercase tracking-[0.32em] mb-3">
            <Lock size={12} /> Secure login
          </div>
          <h2 className="font-serif text-4xl text-[var(--dsf-green)] mb-6">Staff console</h2>

          {/* Tab switch admin/manager */}
          <div className="flex gap-2 mb-6" data-testid="login-tabs">
            <button type="button" onClick={() => onTab("admin")} className={`flex-1 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider ${tab === "admin" ? "bg-[var(--dd-green)] text-white" : "bg-white border border-[var(--dsf-border)] text-[var(--dd-green)]"}`} data-testid="tab-admin">
              Admin
            </button>
            <button type="button" onClick={() => onTab("manager")} className={`flex-1 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider ${tab === "manager" ? "bg-[var(--dd-green)] text-white" : "bg-white border border-[var(--dsf-border)] text-[var(--dd-green)]"}`} data-testid="tab-manager">
              Manager
            </button>
          </div>

          <label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dd-green)] block mb-2">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border border-[var(--dsf-border)] rounded-lg px-4 py-3 mb-5 text-sm focus:border-[var(--dd-green)] bg-white"
            data-testid="admin-login-email"
          />

          <label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dd-green)] block mb-2">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-[var(--dsf-border)] rounded-lg px-4 py-3 mb-8 text-sm focus:border-[var(--dd-green)] bg-white"
            data-testid="admin-login-password"
          />

          <button type="submit" disabled={loading} className="btn-mustard w-full justify-center disabled:opacity-50" data-testid="admin-login-submit">
            {loading ? "Signing in…" : `Sign in as ${tab === "admin" ? "Admin" : "Manager"}`}
          </button>

          <Link to="/" className="block text-center mt-6 text-sm text-[var(--dsf-text-secondary)] hover:text-[var(--dd-mustard)]">
            Back to store
          </Link>
        </form>
      </div>
    </div>
  );
}
